import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserManager, type User } from 'oidc-client-ts';
import { AuthContext, type AuthContextValue, REQUIRED_ROLE, hasRole } from './AuthContext';
import { createAuthConfig } from './authConfig';
import { track } from '../analytics/analyticsService';

interface AuthProviderProps {
  readonly children: React.ReactNode;
}

interface AuthProviderState {
  readonly user: User | null;
  readonly isLoading: boolean;
  readonly isLoggingOut: boolean;
  readonly error: Error | null;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const userManagerRef = useRef<UserManager | null>(null);
  const [state, setState] = useState<AuthProviderState>({
    user: null,
    isLoading: true,
    isLoggingOut: false,
    error: null,
  });

  // Initialize UserManager once
  const getUserManager = useCallback((): UserManager => {
    if (!userManagerRef.current) {
      userManagerRef.current = new UserManager(createAuthConfig());
    }
    return userManagerRef.current;
  }, []);

  // Restore session on mount
  useEffect(() => {
    const userManager = getUserManager();

    const restoreSession = async () => {
      try {
        const user = await userManager.getUser();
        setState({
          user,
          isLoading: false,
          isLoggingOut: false,
          error: null,
        });
      } catch (error) {
        setState({
          user: null,
          isLoading: false,
          isLoggingOut: false,
          error: error instanceof Error ? error : new Error('Failed to restore session'),
        });
      }
    };

    restoreSession();
  }, [getUserManager]);

  // Subscribe to UserManager events
  useEffect(() => {
    const userManager = getUserManager();

    const handleUserLoaded = (user: User) => {
      setState(prev => ({ ...prev, user, error: null }));
    };

    const handleUserUnloaded = () => {
      setState(prev => ({ ...prev, user: null }));
    };

    const handleSilentRenewError = (error: Error) => {
      console.error('Silent renewal error:', error);
      // Don't immediately clear the user - only log the error
      // The user will be cleared when the token actually expires and getAccessToken fails
      setState(prev => ({ ...prev, error }));
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [getUserManager]);

  const login = useCallback(async (returnUrl?: string): Promise<void> => {
    const userManager = getUserManager();
    track('Pocket Admin Login Started');
    await userManager.signinRedirect({
      state: returnUrl ? { returnUrl } : undefined,
    });
  }, [getUserManager]);

  const logout = useCallback(async (): Promise<void> => {
    const userManager = getUserManager();
    const user = await userManager.getUser();
    // Track logout event before initiating logout
    track('Pocket Admin Logout');
    // Mark as logging out to prevent ProtectedRoute from triggering login
    setState(prev => ({ ...prev, isLoggingOut: true }));
    // Pass id_token_hint so Keycloak knows which session to terminate
    await userManager.signoutRedirect({
      id_token_hint: user?.id_token,
    });
  }, [getUserManager]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const userManager = getUserManager();
    try {
      let user = await userManager.getUser();
      
      // If token is expired, try silent renewal
      if (user?.expired) {
        try {
          user = await userManager.signinSilent();
        } catch {
          // Silent renewal failed, trigger full login
          await userManager.signinRedirect();
          return null;
        }
      }
      
      return user?.access_token ?? null;
    } catch {
      return null;
    }
  }, [getUserManager]);

  const handleSigninCallback = useCallback(async (): Promise<User | undefined> => {
    const userManager = getUserManager();
    const user = await userManager.signinCallback();
    return user ?? undefined;
  }, [getUserManager]);

  const contextValue = useMemo<AuthContextValue>(() => ({
    user: state.user,
    isLoading: state.isLoading,
    isLoggingOut: state.isLoggingOut,
    isAuthenticated: state.user !== null,
    hasRequiredRole: hasRole(state.user, REQUIRED_ROLE),
    error: state.error,
    login,
    logout,
    getAccessToken,
    handleSigninCallback,
  }), [state.user, state.isLoading, state.isLoggingOut, state.error, login, logout, getAccessToken, handleSigninCallback]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
